// src/components/LandingPage.jsx
// Public landing page — shown before auth
// Style reference: DRAIP.com.au — dark, authoritative, legal

import { useState, useEffect } from 'react';

const GOLD   = '#C8A84B';
const DARK   = '#0D1B2A';
const TEAL   = '#1A6B7A';
const CREAM  = '#F7F5F0';

// Pre-launch: sign-up is closed in Cognito (15 Oct 2026). Set to true at launch,
// together with re-opening sign-up (close_signup_20261015.py --reopen).
const ACCOUNTS_OPEN = false;
const CA_LABEL = ACCOUNTS_OPEN ? 'Create account →' : 'Accounts open at launch';
const CA_CLOSED_STYLE = ACCOUNTS_OPEN ? {} : { opacity: 0.55, cursor: 'not-allowed' };
// The sample certificate and the costs agreement amendment still describe the old product
// (LitFit score, "erased after each run"). Hidden until both are reissued (15 Oct 2026).
const SHOW_LEGACY_DOWNLOADS = false;

export default function LandingPage({ onSignIn, onCreateAccount }) {
  const caClick = ACCOUNTS_OPEN ? onCreateAccount : undefined;
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: DARK, color: '#FFF', minHeight: '100vh' }}>

      {/* ── Google Fonts ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .nav-link {
          font-size: 13.5px; color: rgba(255,255,255,0.55); text-decoration: none;
          cursor: pointer; transition: color 0.2s; letter-spacing: 0.02em;
        }
        .nav-link:hover { color: rgba(255,255,255,0.9); }

        .btn-primary {
          background: ${GOLD}; color: ${DARK}; border: none;
          padding: 12px 28px; border-radius: 7px; font-size: 13.5px;
          font-weight: 500; font-family: "'DM Sans', sans-serif";
          cursor: pointer; letter-spacing: 0.03em;
          transition: all 0.2s;
        }
        .btn-primary:hover { background: #D4B05A; transform: translateY(-1px); }

        .btn-ghost {
          background: transparent; color: rgba(255,255,255,0.8);
          border: 1px solid rgba(255,255,255,0.2);
          padding: 11px 24px; border-radius: 7px; font-size: 13.5px;
          font-family: "'DM Sans', sans-serif";
          cursor: pointer; letter-spacing: 0.02em;
          transition: all 0.2s;
        }
        .btn-ghost:hover { border-color: rgba(255,255,255,0.5); color: #FFF; }

        .feature-card {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px; padding: 32px 28px;
          transition: all 0.25s;
        }
        .feature-card:hover {
          background: rgba(255,255,255,0.07);
          border-color: rgba(200,168,75,0.3);
          transform: translateY(-3px);
        }

        .step-num {
          font-family: "'Playfair Display', Georgia, serif";
          font-size: 48px; font-weight: 400;
          color: rgba(200,168,75,0.45);
          line-height: 1;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up { animation: fadeUp 0.7s ease forwards; }
        .fade-up-2 { animation: fadeUp 0.7s 0.15s ease both; }
        .fade-up-3 { animation: fadeUp 0.7s 0.3s ease both; }
        .fade-up-4 { animation: fadeUp 0.7s 0.45s ease both; }

        @media (max-width: 768px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .features-grid { grid-template-columns: 1fr !important; }
          .steps-grid { grid-template-columns: 1fr !important; }
          .footer-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Nav ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: '0 48px',
        background: scrolled ? 'rgba(13,27,42,0.97)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : 'none',
        transition: 'all 0.3s',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: 68,
      }}>
        {/* Brand */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 500 }}>
            Calderbank.AI
          </div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 19, color: GOLD, fontWeight: 500, letterSpacing: '0.01em' }}>
            LitFit™
          </div>
        </div>

        {/* Nav links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 36 }}>
          <span className="nav-link" onClick={() => document.getElementById('certificate')?.scrollIntoView({ behavior: 'smooth' })}>
            AI Certificate
          </span>
          <span className="nav-link" onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}>
            How it works
          </span>
          <span className="nav-link" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>
            Features
          </span>
          <span className="nav-link" onClick={() => document.getElementById('who')?.scrollIntoView({ behavior: 'smooth' })}>
            Who it's for
          </span>
          <span className="nav-link" onClick={() => document.getElementById('privilege')?.scrollIntoView({ behavior: 'smooth' })}>
            Privilege & data
          </span>
          <div style={{ display: 'flex', gap: 10, marginLeft: 12 }}>
            <button className="btn-ghost" onClick={onSignIn} style={{ padding: '9px 20px', fontSize: 13 }}>Sign in</button>
            <button className="btn-primary" onClick={caClick} disabled={!ACCOUNTS_OPEN} style={{ padding: '9px 20px', fontSize: 13, ...CA_CLOSED_STYLE }}>{CA_LABEL}</button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        padding: '120px 48px 80px',
        background: `radial-gradient(ellipse at 70% 50%, rgba(26,107,122,0.15) 0%, transparent 60%),
                     radial-gradient(ellipse at 20% 80%, rgba(200,168,75,0.08) 0%, transparent 50%),
                     ${DARK}`,
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
          <div className="hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'flex-start', paddingTop: 40 }}>

            {/* Left */}
            <div>
              {/* Pre-launch notice — remove at launch (February 2027) */}
              <div className="fade-up" style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                maxWidth: '100%', padding: '8px 16px', borderRadius: 999,
                marginBottom: 22,
                border: '1px solid rgba(200,168,75,0.4)',
                background: 'rgba(200,168,75,0.08)',
                fontSize: 13, letterSpacing: '0.03em', lineHeight: 1.4,
                color: GOLD, fontWeight: 500,
              }}>
                <span style={{
                  flex: 'none', width: 7, height: 7, borderRadius: '50%',
                  background: GOLD, boxShadow: '0 0 0 3px rgba(200,168,75,0.2)',
                }} />
                <span>Calderbank.ai is in development, with launch planned for early 2027. Accounts and payments are not yet open.</span>
              </div>

              <h1 className="fade-up" style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 58, fontWeight: 500, lineHeight: 1.08,
                color: '#FFF', marginBottom: 14,
                letterSpacing: '-0.01em',
              }}>
                LitFit™{' '}
                <span style={{ fontSize: 28, fontWeight: 400, color: 'rgba(255,255,255,0.45)', fontStyle: 'normal' }}>
                  by Calderbank.AI
                </span>
              </h1>

              <h2 className="fade-up-2" style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 26, fontWeight: 400, fontStyle: 'italic',
                color: GOLD, marginBottom: 28, lineHeight: 1.35,
              }}>
                Assessing your case's readiness for litigation.
              </h2>

              {/* Platform description — first */}
              <p className="fade-up-3" style={{
                fontSize: 16, color: 'rgba(255,255,255,0.65)', lineHeight: 1.75,
                marginBottom: 16, maxWidth: 520,
              }}>
                LitFit™ by Calderbank.ai produces a{' '}
                <strong style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                  Case Readiness Report
                </strong>{' '}
                for Australian civil disputes — identifying the legal elements of each claim,
                the evidence available and missing, litigation risks and outstanding preparation.
              </p>

              {/* AWS/Bedrock — second */}
              <p className="fade-up-3" style={{
                fontSize: 14.5, color: 'rgba(255,255,255,0.45)', lineHeight: 1.75,
                marginBottom: 16, maxWidth: 520,
              }}>
                Hosted in AWS Sydney (ap-southeast-2). Documents are processed through
                Amazon Bedrock in Australian AWS regions solely to generate your report,
                and are never used to train any AI model.
              </p>

              {/* Corpus — third */}
              <p className="fade-up-3" style={{
                fontSize: 14.5, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7,
                marginBottom: 40, maxWidth: 520,
              }}>
                Analysis applies Australian legal principles.
              </p>

              <div className="fade-up-4" style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <button className="btn-primary" onClick={caClick} disabled={!ACCOUNTS_OPEN} style={{ fontSize: 14, padding: '14px 32px', ...CA_CLOSED_STYLE }}>
                  {CA_LABEL}
                </button>
                <button className="btn-ghost" onClick={onSignIn} style={{ fontSize: 14, padding: '13px 28px' }}>
                  Sign in
                </button>
              </div>

              <div className="fade-up-4" style={{ marginTop: 24, fontSize: 12.5, color: 'rgba(255,255,255,0.25)' }}>
                $50 AUD per Case Readiness Report · For practitioners, may be recoverable as a disbursement
              </div>
            </div>

            {/* Right — mock CRR card, pushed down to sit under heading */}
            <div className="fade-up-3" style={{ paddingTop: 80 }}>
              <div style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 16, padding: '32px 36px',
                backdropFilter: 'blur(8px)',
              }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 20 }}>
                Case Readiness Report · Illustrative example — fictional matter
              </div>

              {/* No overall score: LitFit scores are not published at this stage of
                  development (Tom, 15 Oct 2026). The preview shows element ratings. */}
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>Element ratings</div>

              {/* Element labels — the SOUND / ARGUABLE / WEAK vocabulary the CRR
                  applies to each cause-of-action element. */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 18 }}>
                {[
                  { label: 'Breach — SOUND',      color: '#5BAD7A' },
                  { label: 'Causation — ARGUABLE', color: '#E0B060' },
                  { label: 'Quantum — WEAK',       color: '#C77B7B' },
                ].map(e => (
                  <span key={e.label} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', color: e.color, border: `1px solid ${e.color}33` }}>
                    {e.label}
                  </span>
                ))}
              </div>

              <div style={{ marginTop: 24, padding: '14px 16px', background: 'rgba(200,168,75,0.08)', borderRadius: 8, borderLeft: `3px solid ${GOLD}` }}>
                <div style={{ fontSize: 11, color: GOLD, fontWeight: 500, marginBottom: 5 }}>Key finding</div>
                <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                  In this example, the loss claimed is not yet supported by expert evidence, so a forensic accounting report is the main outstanding preparation item.
                </div>
              </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── AI Certificate ── */}
      <section id="certificate" style={{ padding: '80px 48px', borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {/* ── B13: AI Certificate panel ── */}
          <div className="feature-card" style={{ padding: '32px 36px', textAlign: 'left' }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, fontWeight: 500, color: '#FFF', marginBottom: 12 }}>
              AI Certificate — every Case Readiness Report is accompanied by a certificate recording how it was produced
            </div>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.75, marginBottom: 18 }}>
              Each run automatically generates a Compliance Certificate recording technical
              information about how the report was produced. It does not certify compliance
              with any legal obligation. It records:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px 24px', marginBottom: 20 }}>
              {[
                { t: 'Practice-note information', b: 'Records information relevant to AI practice notes, including Federal Court GPN-AI and NSW Supreme Court SC Gen 23. Whether a practice note is satisfied is a matter for the user.' },
                { t: 'Restricted material', b: 'Records the processing environment used. It does not establish whether compulsorily produced or restricted material may be used — check any undertaking or order before uploading.' },
                { t: 'Cryptographic binding', b: 'A SHA-256 manifest hash ties the certificate to the exact document set analysed.' },
                { t: 'Data handling', b: 'Matter documents are retained for 30 days after the last activity on the matter, unless deleted sooner at your request. Reports and certificates are currently deleted 30 days after they are generated.' },
              ].map(c => (
                <div key={c.t}>
                  <div style={{ fontSize: 13.5, fontWeight: 500, color: GOLD, marginBottom: 5 }}>{c.t}</div>
                  <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>{c.b}</div>
                </div>
              ))}
            </div>
            <div style={{ marginBottom: 20, padding: '14px 18px', background: 'rgba(200,168,75,0.06)', border: '1px solid rgba(200,168,75,0.15)', borderRadius: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 500, color: GOLD }}>Note — use in court documents: </span>
              <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6 }}>
                The Case Readiness Report is an internal working document — not for filing,
                service, or tender. If you elect to incorporate its content into a court
                document, your court's AI practice-note disclosure obligations apply to
                that document.
              </span>
            </div>

            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.35)', lineHeight: 1.6, marginBottom: 20, fontStyle: 'italic' }}>
              The certificate records technical information; whether privilege attaches
              depends on the circumstances of each matter.
            </p>
            {SHOW_LEGACY_DOWNLOADS && <a
              href="/sample-compliance-certificate.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: GOLD, textDecoration: 'none', fontWeight: 500, fontSize: 14 }}
            >
              View a sample certificate →
            </a>}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="how-it-works" style={{ padding: '100px 48px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <SectionEyebrow>How it works</SectionEyebrow>
          <SectionHeading>Four steps to a Case Readiness Report</SectionHeading>

          <div className="steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, marginTop: 56 }}>
            {[
              { n: '01', title: 'Create a matter', body: 'Name your matter and provide a brief overview. Return to the matter and run further reports as it develops.' },
              { n: '02', title: 'Upload your documents', body: 'Select the relevant pleadings, correspondence, evidence and other case documents. PDF or DOCX.' },
              { n: '03', title: 'Receive your report', body: 'Calderbank.ai analyses the material and prepares a Case Readiness Report identifying legal issues, evidentiary gaps and preparation priorities.' },
              { n: '04', title: 'Review and take the next step', body: 'Use the findings to plan further preparation. Discuss the report with your lawyer where applicable, or consider obtaining legal advice before acting.' },
            ].map(s => (
              <div key={s.n} className="feature-card" style={{ padding: '28px 24px' }}>
                <div className="step-num">{s.n}</div>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 18, fontWeight: 500, color: '#FFF', margin: '12px 0 10px', lineHeight: 1.3 }}>
                  {s.title}
                </div>
                <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.45)', lineHeight: 1.65 }}>
                  {s.body}
                </div>
              </div>
            ))}
          </div>

          {SHOW_LEGACY_DOWNLOADS && <div style={{ marginTop: 40, padding: '16px 24px', background: 'rgba(200,168,75,0.06)', border: '1px solid rgba(200,168,75,0.15)', borderRadius: 10, display: 'inline-block' }}>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
              Need to authorise use with a client?{' '}
              <a href="/cdca-amendment.docx" style={{ color: GOLD, textDecoration: 'none', fontWeight: 500 }}>
                Download our pro forma Costs Disclosure and Costs Agreement amendment →
              </a>
            </span>
          </div>}
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" style={{
        padding: '100px 48px',
        background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <SectionEyebrow>Features</SectionEyebrow>
          <SectionHeading>Built for litigation readiness, not generic legal research</SectionHeading>

          <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20, marginTop: 56 }}>
            {[
              {
                icon: '⚖',
                title: 'Element-by-element assessment',
                body: 'Each element of each cause of action is rated SOUND, ARGUABLE or WEAK against the evidence, so you can see where the case is strong, where the gaps are, and which gaps are fixable before you file.',
              },
              {
                icon: '📐',
                title: 'Understand your case\'s readiness for litigation',
                body: 'Calderbank.ai analyses the legal elements of a claim, the available evidence, potential weaknesses and outstanding preparation. Its Case Readiness Report identifies issues requiring attention and practical steps to improve case preparation.',
              },
              {
                icon: '💰',
                title: 'Costs and procedural risks',
                body: 'Identify procedural requirements, potential costs exposure, evidentiary gaps and practical steps relevant to preparing the matter for litigation.',
              },
              {
                icon: '📊',
                title: 'Trial risk factors',
                body: 'Identifies risks that may affect the matter at trial, such as witness evidence, costs and enforcement.',
              },
            ].map(f => (
              <div key={f.title} className="feature-card">
                <div style={{ fontSize: 28, marginBottom: 16 }}>{f.icon}</div>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 17, fontWeight: 500, color: '#FFF', marginBottom: 10, lineHeight: 1.35 }}>
                  {f.title}
                </div>
                <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.45)', lineHeight: 1.65 }}>
                  {f.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who it's for ── */}
      <section id="who" style={{ padding: '100px 48px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
          <div>
            <SectionEyebrow>Who it's for</SectionEyebrow>
            <SectionHeading style={{ marginBottom: 28 }}>Who Calderbank.ai is for</SectionHeading>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', lineHeight: 1.75, marginBottom: 32 }}>
              Calderbank.ai is being developed for Australian legal practitioners, in-house
              legal teams, clients acting under the direction of their lawyers, and people
              representing themselves in civil disputes. Reports are designed to identify
              legal issues, evidentiary gaps, litigation risks and preparation priorities.
              The information and guidance provided will differ according to the user's
              circumstances and the nature of the matter.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                'Solicitors and small firms identifying evidentiary gaps before filing',
                'In-house teams understanding litigation exposure and preparation needs',
                'Barristers testing pleadings and submissions before hearing',
                'People representing themselves who want to understand the issues and evidence in their dispute',
              ].map(item => (
                <div key={item} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ color: GOLD, fontSize: 16, flexShrink: 0, marginTop: 1 }}>→</span>
                  <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing card */}
          <div style={{
            background: 'rgba(200,168,75,0.06)',
            border: '1px solid rgba(200,168,75,0.2)',
            borderRadius: 16, padding: '44px 40px',
          }}>
            <div style={{ fontSize: 11, color: GOLD, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16 }}>
              Pricing
            </div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 52, fontWeight: 500, color: '#FFF', lineHeight: 1, marginBottom: 8 }}>
              $50
              <span style={{ fontSize: 20, fontWeight: 400, color: 'rgba(255,255,255,0.4)', marginLeft: 6 }}>AUD</span>
            </div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', marginBottom: 28 }}>per Case Readiness Report</div>

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 24, marginBottom: 28 }}>
              {[
                'No subscription — pay per report',
                'For practitioners, may be recoverable as a client disbursement',
                'No charge for a report that fails to complete',
              ].map(item => (
                <div key={item} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ color: GOLD, fontSize: 14 }}>✓</span>
                  <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.55)' }}>{item}</span>
                </div>
              ))}
            </div>

            <button className="btn-primary" onClick={caClick} disabled={!ACCOUNTS_OPEN} style={{ width: '100%', padding: '14px 0', fontSize: 14, ...CA_CLOSED_STYLE }}>
              {CA_LABEL}
            </button>
          </div>
        </div>
      </section>

      {/* ── Privilege & data ── */}
      <section id="privilege" style={{
        padding: '100px 48px',
        background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <SectionEyebrow>Privilege & data</SectionEyebrow>
          <SectionHeading>Your matter data stays yours</SectionHeading>

          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, marginBottom: 16, maxWidth: 680, margin: '0 auto 16px' }}>
            LitFit™ is a closed-source platform operated within a secure, Australian-hosted
            environment — AWS Sydney (ap-southeast-2).
          </p>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, marginBottom: 56, maxWidth: 680, margin: '0 auto 56px' }}>
            Documents you upload are processed through Amazon Bedrock solely to generate
            your report. Bedrock does not share them with the model provider or use them to
            train any AI model. Each matter is kept separate from other users' data, within a
            closed environment with strict access controls. Matter documents are retained for
            30 days after the last activity on the matter, unless deleted sooner at your
            request. Reports and certificates are currently deleted 30 days after they are
            generated.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {[
              { label: 'Legal professional privilege', body: 'Reports are designed for use in obtaining legal advice or in actual or anticipated litigation. The platform cannot confer privilege: whether it attaches depends on the circumstances, including whether a lawyer is involved. If you are representing yourself, privilege may not apply to your report or to the documents you upload.' },
              { label: 'Australian hosting', body: 'Matter documents and reports are stored in AWS Sydney and processed in Australian AWS regions. Payment details are handled by Stripe.' },
              { label: 'No AI training', body: 'Your matter data is never used to train, fine-tune, or improve any AI model — by Anthropic, AWS, or Calderbank.AI.' },
            ].map(p => (
              <div key={p.label} className="feature-card" style={{ textAlign: 'left' }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(200,168,75,0.1)', border: '1px solid rgba(200,168,75,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <span style={{ color: GOLD, fontSize: 16 }}>🔒</span>
                </div>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 16, fontWeight: 500, color: '#FFF', marginBottom: 10 }}>
                  {p.label}
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.65 }}>
                  {p.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer CTA ── */}
      <section style={{
        padding: '100px 48px',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        textAlign: 'center',
        background: `radial-gradient(ellipse at 50% 100%, rgba(26,107,122,0.12) 0%, transparent 60%)`,
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{ fontSize: 11, color: GOLD, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 16, fontWeight: 500 }}>
            Calderbank.AI
          </div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 42, fontWeight: 500, color: '#FFF', lineHeight: 1.2, marginBottom: 16 }}>
            Ready to prepare your case?
          </div>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.4)', marginBottom: 36, lineHeight: 1.7 }}>
            Create an account, review our Terms of Service, upload your documents and
            provide a matter summary. Receive a structured Case Readiness Report when
            the analysis is complete.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center' }}>
            <button className="btn-primary" onClick={caClick} disabled={!ACCOUNTS_OPEN} style={{ fontSize: 14, padding: '14px 36px', ...CA_CLOSED_STYLE }}>
              {CA_LABEL}
            </button>
            <button className="btn-ghost" onClick={onSignIn} style={{ fontSize: 14, padding: '13px 28px' }}>
              Sign in
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ padding: '32px 48px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="footer-grid" style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', gap: 16 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)', lineHeight: 1.8 }}>
            LitFit™ and Calderbank.ai™ are trade marks used under licence.<br/>
            Calderbank.ai is operated by Calderbank AI Pty Limited (ACN 697 339 272).
          </div>
          <div style={{ display: 'flex', gap: 24, justifyContent: 'flex-end' }}>
            <a href="/terms.html" target="_blank" style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', textDecoration: 'none' }}>Terms of Service</a>
            <a href="/privacy.html" target="_blank" style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', textDecoration: 'none' }}>Privacy Policy</a>
          </div>
        </div>
      </footer>

    </div>
  );
}

// ── Section helpers ───────────────────────────────────────────────────────────

function SectionEyebrow({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 500, color: GOLD, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 14 }}>
      {children}
    </div>
  );
}

function SectionHeading({ children, style = {} }) {
  return (
    <div style={{
      fontFamily: "'Playfair Display', Georgia, serif",
      fontSize: 36, fontWeight: 500, color: '#FFF',
      lineHeight: 1.2, marginBottom: 16,
      ...style,
    }}>
      {children}
    </div>
  );
}
